import { jwtDecode } from 'jwt-decode';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    FiHome,
    FiSun,
    FiBattery,
    FiCheck,
    FiZap
} from 'react-icons/fi';
import {
    MdElectricalServices,
    MdLocalFireDepartment,
    MdBuildCircle,
    MdCarRental,
} from 'react-icons/md';
import { FiPackage } from 'react-icons/fi';
import { GiSolarPower } from 'react-icons/gi';
import { FiWind } from 'react-icons/fi';
import axios from 'axios';
import AlertMessage from '../../../components/AlertMessage';
import Modal from '../../../components/Modal';
import SolarCalculatorSection from './SolarCalculatorSection';
import ElectricalServicesSection from './ElectricalServicesSection';
import GeneratorServicesSection from './GeneratorServicesSection';
import ContactInformationSection from './ContactInformationSection';
import ReviewSection from './ReviewSection';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://localhost:443',
    timeout: 10000,
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const Quote = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeSection, setActiveSection] = useState('selection');
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [serviceType, setServiceType] = useState(''); // 'solar', 'electrical', 'generator'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isLeavingQuote = useRef(false);
    const [formResetKey, setFormResetKey] = useState(0);
    const electricalSectionRef = useRef(null);
    const contactSectionRef = useRef(null);

    const [shouldRenderSections, setShouldRenderSections] = useState(true);

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);

    const [formData, setFormData] = useState({
        solarData: null,
        electricalData: null,
        generatorData: null,
        contactData: null
    });

    const [isSolarDataValid, setIsSolarDataValid] = useState(true);
    const [completedSections, setCompletedSections] = useState([]);
    const [alert, setAlert] = useState({
        show: false,
        type: 'success',
        title: '',
        message: '',
        duration: 3000
    });

    // Apply pre-selected service passed via navigation state (e.g. from Services page portals)
    useEffect(() => {
        const preSelected = location.state?.preSelectedService;
        if (preSelected && ['solar', 'electrical', 'generator'].includes(preSelected)) {
            setServiceType(preSelected);
            setActiveSection(preSelected);
            setCompletedSections(['selection']);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isTokenExpired = (token) => {
        try {
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            return decoded.exp < currentTime;
        } catch (error) {
            return true;
        }
    };

    const validateToken = useCallback(async (token) => {
        if (!token) return false;
        if (token.includes('.')) {
            return !isTokenExpired(token);
        }
        try {
            const response = await api.get('/api/auth/user/', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.status === 200;
        } catch (error) {
            return false;
        }
    }, []);

    useEffect(() => {
        if (location.pathname !== '/quote') {
            setShouldRenderSections(false);
        } else {
            setShouldRenderSections(true);
        }
    }, [location.pathname]);

    // Listen for tokenExpired custom event (e.g. fired by API interceptors elsewhere)
    useEffect(() => {
        const handleTokenExpired = () => {
            setAlert({
                show: true,
                type: 'warning',
                title: 'Session Expired',
                message: 'Your session has expired. Please log in again.',
                duration: 2500,
            });
            setTimeout(() => navigate('/login', { replace: true }), 2500);
        };
        window.addEventListener('tokenExpired', handleTokenExpired);
        return () => window.removeEventListener('tokenExpired', handleTokenExpired);
    }, [navigate]);

    // Proactive JWT expiry check — runs on mount and every 30 seconds
    useEffect(() => {
        const checkTokenExpiry = () => {
            const token = localStorage.getItem('authToken');
            if (!token || token.length === 40) return; // skip non-JWT tokens

            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const expiresAt = payload.exp * 1000; // convert to ms

                if (Date.now() >= expiresAt) {
                    // Token is already expired — clean up and fire event
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('user');
                    localStorage.removeItem('userId');
                    localStorage.removeItem('username');
                    localStorage.removeItem('is_superuser');
                    localStorage.removeItem('authTokenStoredAt');
                    setIsAuthenticated(false);
                    window.dispatchEvent(new CustomEvent('tokenExpired'));
                }
            } catch {
                // Malformed token — treat as expired
                window.dispatchEvent(new CustomEvent('tokenExpired'));
            }
        };

        checkTokenExpiry(); // Check immediately on mount
        const interval = setInterval(checkTokenExpiry, 30_000); // Then every 30s
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const checkAuthentication = async () => {
            const token = localStorage.getItem('authToken');

            if (token) {
                // Legacy non-JWT token (length-40 DRF token) — accept as-is
                if (token.length === 40 && !token.includes('.')) {
                    setIsAuthenticated(true);
                    setAuthChecked(true);
                    return;
                }
                // JWT token — await the async validation
                const valid = await validateToken(token);
                if (!valid) {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    setShowAuthModal(true);
                    setAuthChecked(true);
                    return;
                }
                setIsAuthenticated(true);
            } else {
                setShowAuthModal(true);
            }
            setAuthChecked(true);
        };

        checkAuthentication();
    }, [navigate, validateToken]);

    const handleAuthModalConfirm = () => {
        setShowAuthModal(false);
        navigate('/login');
    };

    const handleAuthModalCancel = () => {
        setShowAuthModal(false);
        navigate('/');
    };

    useEffect(() => {
        if (isAuthenticated) {
            const savedData = localStorage.getItem('quoteFormData');
            if (savedData) {
                try {
                    const parsedData = JSON.parse(savedData);
                    setFormData({
                        solarData: parsedData.solarData || null,
                        electricalData: parsedData.electricalData || null,
                        generatorData: parsedData.generatorData || null,
                        contactData: parsedData.contactData || null
                    });
                    setServiceType(parsedData.serviceType || '');

                    if (parsedData.serviceType) {
                        if (parsedData.serviceType === 'solar' && parsedData.solarData) {
                            setActiveSection('contact');
                            setCompletedSections(['solar']);
                        } else if (parsedData.serviceType === 'electrical' && parsedData.electricalData) {
                            setActiveSection('contact');
                            setCompletedSections(['electrical']);
                        } else if (parsedData.serviceType === 'generator' && parsedData.generatorData) {
                            setActiveSection('contact');
                            setCompletedSections(['generator']);
                        }
                    }
                } catch (error) {
                    localStorage.removeItem('quoteFormData');
                }
            }
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && formData && !isLeavingQuote.current) {
            localStorage.setItem(
                'quoteFormData',
                JSON.stringify({ ...formData, serviceType })
            );
        }
    }, [formData, serviceType, isAuthenticated]);

    const showAlert = (type, title, message, duration = 3000) => {
        setAlert({ show: true, type, title, message, duration });
    };

    const hideAlert = () => {
        setAlert(prev => ({ ...prev, show: false }));
    };

    const handleSolarDataUpdate = useCallback((data) => {
        setFormData(prev => ({ ...prev, solarData: data }));
    }, []);

    const handleElectricalDataUpdate = useCallback((data) => {
        setFormData(prev => ({ ...prev, electricalData: data }));
    }, []);

    const handleGeneratorDataUpdate = useCallback((data) => {
        setFormData(prev => ({ ...prev, generatorData: data }));
    }, []);

    const handleContactDataUpdate = useCallback((data) => {
        setFormData(prev => ({ ...prev, contactData: data }));
    }, []);

    const clearFormData = useCallback(() => {
        setFormData({
            solarData: null,
            electricalData: null,
            generatorData: null,
            contactData: null
        });
        setServiceType('');
        setActiveSection('selection');
        setIsSolarDataValid(true);
        setFormSubmitted(false);
        setCompletedSections([]);
        setFormResetKey(prev => prev + 1);
        localStorage.removeItem('quoteFormData');
    }, []);

    useEffect(() => {
        return () => {
            clearFormData();
        };
    }, [clearFormData]);

    /**
     * After a successful quotation submission, silently update the user's profile
     * with any contact fields they manually entered — but only for fields that were
     * blank in their stored profile (i.e. fields that were editable in the form).
     * If both phone and address already existed on the profile, nothing is sent.
     */
    const syncProfileIfNeeded = async (contactData, token) => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const storedProfile = storedUser.profile || {};

        const missingPhone = !storedProfile.phone && contactData?.phone;
        const missingAddress = !storedProfile.address && contactData?.address;

        if (!missingPhone && !missingAddress) return; // nothing to update

        const payload = {};
        if (missingPhone) payload.phone = contactData.phone;
        if (missingAddress) payload.address = contactData.address;

        try {
            const profileResponse = await api.patch('/rest-auth/update-profile/', payload, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            // Persist the updated profile back into localStorage so the rest of
            // the app (and the next Refresh from Profile call) sees the new data.
            const updatedUser = profileResponse.data?.user;
            if (updatedUser) {
                const merged = {
                    ...storedUser,
                    ...updatedUser,
                    profile: {
                        ...storedProfile,
                        ...updatedUser.profile,
                        // Ensure the flat fields we just saved are reflected too
                        ...(missingPhone ? { phone: contactData.phone } : {}),
                        ...(missingAddress ? { address: contactData.address } : {}),
                    }
                };
                localStorage.setItem('user', JSON.stringify(merged));
            } else {
                // Fallback: patch localStorage manually if response doesn't include user
                const patched = {
                    ...storedUser,
                    profile: {
                        ...storedProfile,
                        ...(missingPhone ? { phone: contactData.phone } : {}),
                        ...(missingAddress ? { address: contactData.address } : {}),
                    }
                };
                localStorage.setItem('user', JSON.stringify(patched));
            }
        } catch (err) {
            // Non-fatal — the quotation was already submitted successfully.
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const token = localStorage.getItem('authToken');

        if (!token) {
            showAlert('error', 'Authentication Error', 'Session expired. Please log in again.');
            setTimeout(() => { navigate('/login'); }, 2000);
            setIsSubmitting(false);
            return;
        }

        try {
            const submissionData = {
                service_type: serviceType,
                contact_information: formData.contactData || {},
            };

            if (serviceType === 'solar') {
                submissionData.solar_calculator = formData.solarData || {};
            }

            if (serviceType === 'electrical') {
                submissionData.electrical_services = formData.electricalData || {};
            }

            if (serviceType === 'generator') {
                submissionData.generator_services = formData.generatorData || {};
            }
            await api.post('/api/quotations/', submissionData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            // Silently backfill profile with any contact data the user just entered
            await syncProfileIfNeeded(formData.contactData, token);

            showAlert('success', 'Success!', 'Your quotation request has been submitted successfully.');
            setFormSubmitted(true);
            clearFormData();

        } catch (error) {

            if (error.response?.status === 401) {
                localStorage.removeItem('authToken');
                localStorage.removeItem('user');
                setShowAuthModal(true);
            } else if (error.response?.status === 400) {
                const errorDetails = error.response?.data;
                let errorMessage = 'Please check your form data and try again.';
                // AFTER
                if (typeof errorDetails === 'object') {
                    const flattenErrors = (obj, prefix = '') => {
                        return Object.entries(obj).flatMap(([field, val]) => {
                            const key = prefix ? `${prefix}.${field}` : field;
                            if (Array.isArray(val)) return [`${key}: ${val.join(', ')}`];
                            if (typeof val === 'object' && val !== null) return flattenErrors(val, key);
                            return [`${key}: ${val}`];
                        });
                    };
                    errorMessage = flattenErrors(errorDetails).join('\n') || errorMessage;
                } else if (typeof errorDetails === 'string') {
                    errorMessage = errorDetails;
                }
                showAlert('error', 'Invalid Form Data', errorMessage, 8000);
            } else {
                showAlert('error', 'Submission Failed',
                    error.response?.data?.message || error.message ||
                    'An error occurred while submitting your quotation. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!authChecked) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Checking authentication...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 md:pt-24">
            {showAuthModal && (
                <Modal
                    title="Authentication Required"
                    message="Please log in to access the quotation service. Would you like to log in now?"
                    onClick={handleAuthModalCancel}
                    altButton={
                        <button
                            onClick={handleAuthModalConfirm}
                            type="button"
                            className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-green-700 sm:ml-3 sm:w-auto"
                        >
                            Go to Login
                        </button>
                    }
                />
            )}

            {alert.show && (
                <AlertMessage
                    type={alert.type}
                    title={alert.title}
                    message={alert.message}
                    duration={alert.duration}
                    onDismiss={hideAlert}
                />
            )}

            <div className="section">
                <section className="page-title">
                    <div className="container mx-auto px-4">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                            <GiSolarPower className="text-4xl text-green-600" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold mb-4">Get Your Free Energy Quotation</h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Transform your energy consumption with solar power and professional electrical services
                        </p>
                    </div>
                </section>
            </div>

            <div className="container mx-auto px-4">
                {/* Progress Steps */}
                <div className="max-w-8xl mx-auto mb-12">
                    <div className="flex justify-between relative">
                        {serviceType ?
                            (serviceType === 'solar' ?
                                ['solar', 'contact', 'review'] :
                                serviceType === 'electrical' ?
                                    ['electrical', 'contact', 'review'] :
                                    ['generator', 'contact', 'review']
                            ).map((step, index) => (
                                <div key={step} className="flex flex-col items-center z-10">
                                    <button
                                        onClick={() => {
                                            if (completedSections.includes(step) || step === activeSection) {
                                                setActiveSection(step);
                                            }
                                        }}
                                        disabled={!completedSections.includes(step) && step !== activeSection}
                                        title={
                                            !completedSections.includes(step) && step !== activeSection
                                                ? 'Complete the current step first'
                                                : undefined
                                        }
                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold transition-all duration-300 ${activeSection === step
                                                ? serviceType === 'electrical'
                                                    ? 'bg-blue-600 scale-110 shadow-lg'
                                                    : serviceType === 'generator'
                                                        ? 'bg-orange-600 scale-110 shadow-lg'
                                                        : 'bg-green-600 scale-110 shadow-lg'
                                                : completedSections.includes(step)
                                                    ? serviceType === 'electrical'
                                                        ? 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                                                        : serviceType === 'generator'
                                                            ? 'bg-orange-500 hover:bg-orange-600 cursor-pointer'
                                                            : 'bg-green-500 hover:bg-green-600 cursor-pointer'
                                                    : 'bg-gray-300 cursor-not-allowed'
                                            }`}
                                    >
                                        {index + 1}
                                    </button>
                                    <span className="mt-2 text-sm font-medium capitalize text-gray-700">{step}</span>
                                </div>
                            )) :
                            ['selection'].map((step) => (
                                <div key={step} className="flex flex-col items-center z-10">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${activeSection === 'selection'
                                        ? 'bg-green-600 scale-110 shadow-lg'
                                        : 'bg-green-500'
                                        }`}>
                                        1
                                    </div>
                                    <span className="mt-2 text-sm font-medium capitalize text-gray-700">{step}</span>
                                </div>
                            ))
                        }
                        {serviceType && (
                            <div className="absolute top-6 left-12 right-12 h-1 bg-gray-200 -z-10">
                                <div
                                    className={`h-full transition-all duration-500 ${serviceType === 'electrical' ? 'bg-blue-500' : serviceType === 'generator' ? 'bg-orange-500' : 'bg-green-500'}`}
                                    style={{
                                        width: serviceType === 'solar'
                                            ? `${(['solar', 'contact', 'review'].indexOf(activeSection) / 2) * 100}%`
                                            : serviceType === 'generator'
                                                ? `${(['generator', 'contact', 'review'].indexOf(activeSection) / 2) * 100}%`
                                                : `${(['electrical', 'contact', 'review'].indexOf(activeSection) / 2) * 100}%`
                                    }}
                                ></div>
                            </div>
                        )}
                    </div>
                </div>

                {!formSubmitted ? (
                    <form onSubmit={handleSubmit} className="max-w-8xl mx-auto mb-6">
                        {/* Service Selection */}
                        {activeSection === 'selection' && (
                            <div className="bg-white rounded-xl shadow-lg p-8">
                                {/* Back to Home */}
                                <div className="mb-6">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/')}
                                        className="flex items-center text-gray-600 hover:text-gray-800 font-semibold transition-colors duration-300"
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        Back to Home
                                    </button>
                                </div>

                                <div className="text-center mb-8">
                                    <h2 className="text-3xl font-bold text-gray-800 mb-4">What services are you interested in?</h2>
                                    <p className="text-gray-600">Select a service to get a customized quotation</p>
                                </div>

                                <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                                    {/* Solar Only */}
                                    <div className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${serviceType === 'solar'
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-gray-300 hover:border-green-300 hover:shadow-lg'
                                        }`}
                                        onClick={() => setServiceType('solar')}>
                                        <div className="flex flex-col items-center text-center">
                                            <div className="p-4 bg-green-100 rounded-full mb-4">
                                                <FiSun className="text-4xl text-green-600" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800 mb-2">Solar Services</h3>
                                            <p className="text-gray-600 mb-4">Harness the power of the sun</p>
                                            <ul className="text-left space-y-2 mb-6">
                                                <li className="flex items-start">
                                                    <FiSun className="text-green-600 mr-2 mt-1 flex-shrink-0" />
                                                    <span>Complete solar installations</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <FiBattery className="text-green-600 mr-2 mt-1 flex-shrink-0" />
                                                    <span>Battery backup systems</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <MdLocalFireDepartment className="text-green-600 mr-2 mt-1 flex-shrink-0" />
                                                    <span>Energy-efficient solutions</span>
                                                </li>
                                            </ul>
                                            <div className={`px-6 py-2 rounded-full ${serviceType === 'solar'
                                                ? 'bg-green-600 text-white'
                                                : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                Select Solar
                                            </div>
                                        </div>
                                    </div>

                                    {/* Electrical Only */}
                                    <div className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${serviceType === 'electrical'
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-300 hover:border-blue-300 hover:shadow-lg'
                                        }`}
                                        onClick={() => setServiceType('electrical')}>
                                        <div className="flex flex-col items-center text-center">
                                            <div className="p-4 bg-blue-100 rounded-full mb-4">
                                                <FiZap className="text-4xl text-blue-600" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800 mb-2">Electrical Services</h3>
                                            <p className="text-gray-600 mb-4">Professional electrical solutions</p>
                                            <ul className="text-left space-y-2 mb-6">
                                                <li className="flex items-start">
                                                    <MdElectricalServices className="text-blue-600 mr-2 mt-1 flex-shrink-0" />
                                                    <span>Complete rewiring</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <FiZap className="text-blue-600 mr-2 mt-1 flex-shrink-0" />
                                                    <span>Installation & repairs</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <FiHome className="text-blue-600 mr-2 mt-1 flex-shrink-0" />
                                                    <span>Safety inspections</span>
                                                </li>
                                            </ul>
                                            <div className={`px-6 py-2 rounded-full ${serviceType === 'electrical'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                Select Electrical
                                            </div>
                                        </div>
                                    </div>

                                    {/* Generator Services */}
                                    <div className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${serviceType === 'generator'
                                        ? 'border-orange-500 bg-orange-50'
                                        : 'border-gray-300 hover:border-orange-300 hover:shadow-lg'
                                        }`}
                                        onClick={() => setServiceType('generator')}>
                                        <div className="flex flex-col items-center text-center">
                                            <div className="p-4 bg-orange-100 rounded-full mb-4">
                                                <FiWind className="text-4xl text-orange-600" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800 mb-2">Generator Services</h3>
                                            <p className="text-gray-600 mb-4">Reliable backup power solutions</p>
                                            <ul className="text-left space-y-2 mb-6">
                                                <li className="flex items-start">
                                                    <FiPackage className="text-orange-600 mr-2 mt-1 flex-shrink-0" />
                                                    <span>Generator supply & installation</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <MdBuildCircle className="text-orange-600 mr-2 mt-1 flex-shrink-0" />
                                                    <span>Maintenance & servicing</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <MdCarRental className="text-orange-600 mr-2 mt-1 flex-shrink-0" />
                                                    <span>Generator hire & rental</span>
                                                </li>
                                            </ul>
                                            <div className={`px-6 py-2 rounded-full ${serviceType === 'generator'
                                                ? 'bg-orange-600 text-white'
                                                : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                Select Generator
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                <div className="text-center mt-8">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (serviceType) {
                                                setCompletedSections(prev =>
                                                    prev.includes('selection') ? prev : [...prev, 'selection']
                                                );
                                                setActiveSection(
                                                    serviceType === 'solar' ? 'solar' :
                                                        serviceType === 'electrical' ? 'electrical' :
                                                            serviceType === 'generator' ? 'generator' : 'solar'
                                                );
                                            }
                                        }}
                                        disabled={!serviceType}
                                        className={`px-8 py-3 rounded-lg font-semibold transition-colors duration-300 ${serviceType === 'solar'
                                                ? 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg'
                                                : serviceType === 'electrical'
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                                                    : serviceType === 'generator'
                                                        ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-md hover:shadow-lg'
                                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            }`}
                                    >
                                        Continue to {serviceType === 'solar' ? 'Solar Calculator' : serviceType === 'generator' ? 'Generator Form' : 'Electrical Form'}


                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Solar Calculator Section */}
                        {shouldRenderSections && activeSection === 'solar' &&
                            <SolarCalculatorSection
                                key={`solar-${formResetKey}`}
                                setIsSolarDataValid={setIsSolarDataValid}
                                onDataUpdate={handleSolarDataUpdate}
                                initialData={formData.solarData}
                            />
                        }

                        {/* Electrical Services Section */}
                        {shouldRenderSections && activeSection === 'electrical' &&
                            <ElectricalServicesSection
                                key={`electrical-${formResetKey}`}
                                ref={electricalSectionRef}
                                onDataUpdate={handleElectricalDataUpdate}
                                initialData={formData.electricalData}
                            />
                        }

                        {/* Generator Services Section */}
                        {shouldRenderSections && activeSection === 'generator' && serviceType === 'generator' &&
                            <GeneratorServicesSection
                                key={`generator-${formResetKey}`}
                                onDataUpdate={handleGeneratorDataUpdate}
                                initialData={formData.generatorData}
                            />
                        }

                        {/* Contact Information Section */}
                        {shouldRenderSections && activeSection === 'contact' &&
                            <ContactInformationSection
                                key={`contact-${formResetKey}`}
                                ref={contactSectionRef}
                                onDataUpdate={handleContactDataUpdate}
                                initialData={formData.contactData}
                            />
                        }

                        {/* Review & Submit Section */}
                        {shouldRenderSections && activeSection === 'review' && (
                            <ReviewSection
                                serviceType={serviceType}
                                solarData={formData.solarData}
                                electricalData={formData.electricalData}
                                generatorData={formData.generatorData}
                                contactData={formData.contactData}
                            />
                        )}

                        {/* Navigation Buttons */}
                        {activeSection !== 'selection' && (
                            <div className="flex justify-between section border-t border-gray-200 mt-8 pt-8">
                                <div className="flex space-x-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const sections = serviceType === 'solar'
                                                ? ['solar', 'contact', 'review']
                                                : serviceType === 'generator'
                                                    ? ['generator', 'contact', 'review']
                                                    : ['electrical', 'contact', 'review'];

                                            const currentIndex = sections.indexOf(activeSection);
                                            if (currentIndex > 0) {
                                                setActiveSection(sections[currentIndex - 1]);
                                            } else if (currentIndex === 0) {
                                                setActiveSection('selection');
                                            }
                                        }}
                                        className="px-8 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-300"
                                        disabled={isSubmitting}
                                    >
                                        Back
                                    </button>

                                    <button
                                        type="button"
                                        onClick={clearFormData}
                                        className="px-6 py-3 border-2 border-red-300 text-red-700 font-semibold rounded-lg hover:bg-red-50 transition-colors duration-300"
                                        disabled={isSubmitting}
                                    >
                                        Reset Form
                                    </button>
                                </div>

                                <button
                                    type={activeSection === 'review' ? 'submit' : 'button'}
                                    onClick={(e) => {
                                        if (activeSection !== 'review') {
                                            e.preventDefault();

                                            // Validate current section before advancing
                                            if (activeSection === 'electrical') {
                                                const valid = electricalSectionRef.current?.validate();
                                                if (!valid) {
                                                    showAlert(
                                                        'error',
                                                        'Service Required',
                                                        'Please select at least one electrical service before continuing.',
                                                        0
                                                    );
                                                    return;
                                                }
                                            }

                                            if (activeSection === 'contact') {
                                                const valid = contactSectionRef.current?.validate();
                                                if (!valid) {
                                                    showAlert(
                                                        'error',
                                                        'Missing Information',
                                                        'Please fill in all required fields (Full Name, Email, Phone Number, Property Address, and Preferred Date) before continuing.',
                                                        0
                                                    );
                                                    return;
                                                }
                                            }

                                            const sections = serviceType === 'solar'
                                                ? ['solar', 'contact', 'review']
                                                : serviceType === 'generator'
                                                    ? ['generator', 'contact', 'review']
                                                    : ['electrical', 'contact', 'review'];
                                            const currentIndex = sections.indexOf(activeSection);
                                            if (currentIndex < sections.length - 1) {
                                                const nextSection = sections[currentIndex + 1];
                                                setCompletedSections(prev =>
                                                    prev.includes(activeSection) ? prev : [...prev, activeSection]
                                                );
                                                setActiveSection(nextSection);
                                            }
                                        }
                                    }}
                                    disabled={((activeSection === 'solar' || (serviceType === 'solar' && activeSection === 'solar')) && (!isSolarDataValid || isSubmitting)) || (activeSection !== 'solar' && isSubmitting)}
                                    className={`px-8 py-3 font-semibold rounded-lg transition-colors duration-300 shadow-md ${((activeSection === 'solar' || (serviceType === 'solar' && activeSection === 'solar')) && (!isSolarDataValid || isSubmitting)) || (activeSection !== 'solar' && isSubmitting)
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg'
                                        }`}
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Submitting...
                                        </span>
                                    ) : activeSection === 'review' ? 'Submit Quotation Request' : 'Continue'}
                                </button>
                            </div>
                        )}
                    </form>
                ) : (
                    <div className="max-w-2xl mx-auto text-center py-16">
                        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                            <FiCheck className="text-5xl text-green-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">Thank You!</h2>
                        <p className="text-xl text-gray-600 mb-8">
                            Your quotation request has been submitted successfully. Our energy expert will contact you within 24 hours to discuss your {serviceType} needs.
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300"
                        >
                            Return to Home
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Quote;